import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Send, Sparkles, User } from 'lucide-react';
import { DUTI_AI_AVATAR } from '../../lib/brand';
import api, { authFetch } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { ChatMarkdown } from '../../components/ChatMarkdown';
import { ChatRetryNotice, chatFailure, chatHttpError } from '../../components/duti/ChatRetryNotice';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Duti AI, condensed for one-handed mobile use — general pharmacy/drug Q&A
 * plus answers scoped to this staff member's branch (stock, sales, orders).
 * Reuses the exact same streaming endpoint as the desktop Duti AI page. */
export default function StaffAppDutiPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [failure, setFailure] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'end' }); }, [messages]);

  const newChat = () => { setMessages([]); setSessionId(null); setInput(''); setFailure(null); };

  const sendMessage = async (resend) => {
    const text = (typeof resend === 'string' ? resend : input).trim();
    if (!text || streaming) return;
    const dropFailed = !!failure;
    setFailure(null);
    setMessages((prev) => [...(dropFailed ? prev.slice(0, -1) : prev), { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setInput('');
    setStreaming(true);
    try {
      const res = await authFetch(`${API_URL}/api/claude/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: text, session_id: sessionId, model: 'sonnet', purpose: 'staff_assistant' }),
      });
      if (!res.ok) throw await chatHttpError(res);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let full = '';
      let streamError = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.type === 'session' && evt.session_id) setSessionId(evt.session_id);
            else if (evt.type === 'delta') {
              full += evt.content;
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === 'assistant') updated[updated.length - 1] = { ...last, content: last.content + evt.content };
                return updated;
              });
            } else if (evt.type === 'error') streamError = evt.message || 'Duti AI couldn\'t answer just now.';
          } catch { /* skip malformed SSE lines */ }
        }
      }
      if (streamError && !full) throw Object.assign(new Error(streamError), { status: 500 });
    } catch (e) {
      setFailure(chatFailure(e, text));
      setMessages((prev) => prev.slice(0, -1));
    } finally { setStreaming(false); }
  };

  const retry = (text) => sendMessage(text);

  return (
    <div className="flex flex-col h-[calc(100vh-57px-64px)]" data-testid="staff-app-duti-page">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border shrink-0">
        <span className="flex items-center gap-1.5 text-sm font-bold"><Sparkles className="w-4 h-4 text-primary" /> Duti AI</span>
        <Button variant="ghost" size="sm" className="text-xs h-7" onClick={newChat} data-testid="staff-app-duti-new-chat">
          <Plus className="w-3.5 h-3.5 mr-1" /> New chat
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground px-6" data-testid="staff-app-duti-empty">
            <Sparkles className="w-10 h-10 mb-3 opacity-30" />
            <p className="text-sm font-medium">Ask Duti AI anything</p>
            <p className="text-xs mt-1">Drug questions, or your pharmacy's own stock, sales and order numbers.</p>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-2.5 ${msg.role === 'user' ? 'justify-end' : ''}`} data-testid={`staff-app-duti-msg-${i}`}>
            {msg.role === 'assistant' && (
              <img src={DUTI_AI_AVATAR} alt="Duti AI" className="w-7 h-7 rounded-full shrink-0" data-testid="staff-app-duti-avatar" />
            )}
            <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
              {msg.role === 'assistant' && msg.content ? <ChatMarkdown content={msg.content} internalLinks={false} /> : (
                <div className="whitespace-pre-wrap">{msg.content || (streaming && i === messages.length - 1 ? '…' : '')}</div>
              )}
            </div>
            {msg.role === 'user' && (
              <span className="w-7 h-7 rounded-full bg-foreground/10 flex items-center justify-center shrink-0"><User className="w-4 h-4" /></span>
            )}
          </div>
        ))}
        {failure && !streaming && (
          <ChatRetryNotice key={`${failure.text}-${failure.message}`} failure={failure} onRetry={retry} testId="staff-app-duti-retry" signInPath="/staff-app/login" />
        )}
        <div ref={bottomRef} />
      </div>

      <div className="flex items-end gap-2 px-3 py-2.5 border-t border-border shrink-0">
        <Textarea value={input} onChange={(e) => setInput(e.target.value)} disabled={streaming}
          placeholder="Ask Duti AI…" rows={1} className="min-h-[40px] max-h-28 resize-none" data-testid="staff-app-duti-input"
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} />
        <Button size="icon" onClick={sendMessage} disabled={!input.trim() || streaming} data-testid="staff-app-duti-send">
          {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}
